////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.4                                                            //
//  Summary: Route table — data router, lazy-loaded admin subtree           //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';

import { ConfigProvider } from '@/config';
import { PublicLayout } from '@/layouts/PublicLayout';
import { Home } from '@/pages/public/Home';
import { About } from '@/pages/public/About';
import { Projects } from '@/pages/public/Projects';
import { ProjectDetail } from '@/pages/public/ProjectDetail';
import { Careers } from '@/pages/public/Careers';
import { News } from '@/pages/public/News';
import { NewsDetail } from '@/pages/public/NewsDetail';
import { CareerDetail } from '@/pages/public/CareerDetail';
import { Contact } from '@/pages/public/Contact';
import { Legal } from '@/pages/public/Legal';
import { NotFound } from '@/pages/public/NotFound';
import { LegacyNewsItemRedirect } from '@/pages/public/LegacyNewsItemRedirect';
import { ToastProvider } from '@/components/feedback/ToastProvider';

// The /admin/* subtree is loaded ENTIRELY through `lazy` route fields — nothing
// under pages/admin/ or layouts/AdminLayout is imported statically above. This is
// what keeps TipTap and @simplewebauthn/browser out of the public entry chunk
// (1.0.1.md §3.3/§3.6's build-manifest acceptance criterion). The edge Caddy gates
// network access to /admin* by trusted subnet; that is a SEPARATE control from the
// session-auth gate AdminLayout owns from 1.0.2 onward — this router does not
// conflate the two.
const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/about', element: <About /> },
      { path: '/projects', element: <Projects /> },
      { path: '/project/:slug', element: <ProjectDetail /> },
      { path: '/careers', element: <Careers /> },
      { path: '/careers/:slug', element: <CareerDetail /> },
      { path: '/news', element: <News /> },
      { path: '/news/:slug', element: <NewsDetail /> },
      { path: '/contact', element: <Contact /> },
      { path: '/privacy-cookies-terms-conditions', element: <Legal /> },

      // Legacy paths from the old site — permanent redirects so existing inbound
      // links and search results survive (1.0.1.md §3.3).
      { path: '/news-updates', element: <Navigate to="/news" replace /> },
      { path: '/news-item/:slug', element: <LegacyNewsItemRedirect /> },

      { path: '*', element: <NotFound /> },
    ],
  },
  {
    path: '/admin',
    lazy: async () => {
      const { AdminLayout } = await import('@/layouts/AdminLayout');
      return { Component: AdminLayout };
    },
    children: [
      {
        path: 'login',
        lazy: async () => {
          const { Login } = await import('@/pages/admin/Login');
          return { Component: Login };
        },
      },

      // Everything else under /admin requires a live session. ProtectedRoute reads
      // AdminLayout's <SessionProvider> and redirects to /admin/login on 401/no
      // session — /admin/login itself sits OUTSIDE this guard so the initial "not
      // signed in yet" check on that route does not redirect it to itself.
      {
        lazy: async () => {
          const { ProtectedRoute } = await import('@/layouts/ProtectedRoute');
          return { Component: ProtectedRoute };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { Dashboard } = await import('@/pages/admin/Dashboard');
              return { Component: Dashboard };
            },
          },
          {
            path: 'pages',
            lazy: async () => {
              const { PagesList } = await import('@/pages/admin/PagesList');
              return { Component: PagesList };
            },
          },
          {
            path: 'pages/:id',
            lazy: async () => {
              const { PageEdit } = await import('@/pages/admin/PageEdit');
              return { Component: PageEdit };
            },
          },
          {
            path: 'news',
            lazy: async () => {
              const { NewsList } = await import('@/pages/admin/NewsList');
              return { Component: NewsList };
            },
          },
          {
            path: 'news/:id',
            lazy: async () => {
              const { NewsEdit } = await import('@/pages/admin/NewsEdit');
              return { Component: NewsEdit };
            },
          },
          {
            path: 'projects',
            lazy: async () => {
              const { ProjectsList } = await import('@/pages/admin/ProjectsList');
              return { Component: ProjectsList };
            },
          },
          {
            path: 'projects/:id',
            lazy: async () => {
              const { ProjectEdit } = await import('@/pages/admin/ProjectEdit');
              return { Component: ProjectEdit };
            },
          },
          {
            path: 'careers',
            lazy: async () => {
              const { CareersList } = await import('@/pages/admin/CareersList');
              return { Component: CareersList };
            },
          },
          {
            path: 'careers/:id',
            lazy: async () => {
              const { CareerEdit } = await import('@/pages/admin/CareerEdit');
              return { Component: CareerEdit };
            },
          },
          {
            path: 'media',
            lazy: async () => {
              const { Media } = await import('@/pages/admin/Media');
              return { Component: Media };
            },
          },
        ],
      },
    ],
  },
]);

export function App () {
  return (
    <ConfigProvider>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </ConfigProvider>
  );
}
